import React from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  Chip,
  useTheme,
} from '@mui/material';
import {
  Speed,
  AccountBalance,
  FolderZip,
  Layers,
  Assessment,
  HistoryEdu,
} from '@mui/icons-material';

interface FeatureItem {
  icon: React.ReactElement;
  badge: string;
  title: string;
  description: string;
  highlightColor: string;
}

const ukFeatures: FeatureItem[] = [
  {
    icon: <Speed sx={{ fontSize: 32 }} />,
    badge: 'SENSITIVITY MATRIX',
    title: 'Automated 1D & 2D Sensitivity Engine',
    description: 'Calculate 5x5 to 20x20 multi-variable sensitivity matrices for exit yield, rent growth, and capex inflation across complex UK underwriting models.',
    highlightColor: '#047857',
  },
  {
    icon: <AccountBalance sx={{ fontSize: 32 }} />,
    badge: 'UK COMPLIANT',
    title: 'Automated UK SDLT & Tax Engine',
    description: 'Built-in HMRC Stamp Duty Land Tax (SDLT) slab calculator for commercial & residential property acquisitions, plus lease NPV tax calculations.',
    highlightColor: '#1e40af',
  },
  {
    icon: <HistoryEdu sx={{ fontSize: 32 }} />,
    badge: 'REVISION CONTROL',
    title: 'Model Version Control & Visual Diffs',
    description: 'Track underwriting revisions automatically. Compare investment committee drafts side-by-side with instant formula parameter diffs.',
    highlightColor: '#6d28d9',
  },
  {
    icon: <Layers sx={{ fontSize: 32 }} />,
    badge: 'FINANCIAL STRUCTURE',
    title: 'UK Debt & JV Waterfall Sizing',
    description: 'Structure senior debt linked to SONIA/BoE base rate, mezzanine debt tranches, covenant checks (DSCR, Debt Yield), and European JV waterfall returns.',
    highlightColor: '#b45309',
  },
  {
    icon: <Assessment sx={{ fontSize: 32 }} />,
    badge: 'INSTITUTIONAL AUDIT',
    title: 'Audit-Ready Valuation & Cashflow Exports',
    description: 'Generate audit-ready quarterly cashflows, discounted cash flow (DCF) valuations, and investment committee decks formatted to UK institutional standards.',
    highlightColor: '#047857',
  },
  {
    icon: <FolderZip sx={{ fontSize: 32 }} />,
    badge: 'DOCUMENT HUB',
    title: 'Centralized Asset Document Storage',
    description: 'Organize deal underwriting workbooks, lender term sheets, valuation reports, and IC presentations securely in one cloud workspace.',
    highlightColor: '#1e40af',
  },
];

const UkFeaturesGrid: React.FC = () => {
  const theme = useTheme();

  return (
    <Box id="features-section" sx={{ py: 12, bgcolor: '#ffffff', position: 'relative' }}>
      <Container maxWidth="lg">
        {/* Header */}
        <Box sx={{ textAlign: 'center', mb: 8 }}>
          <Chip
            label="BUILT FOR UK REAL ESTATE INSTITUTIONS"
            sx={{
              bgcolor: 'rgba(4, 120, 87, 0.08)',
              border: '1px solid rgba(4, 120, 87, 0.2)',
              color: '#047857',
              px: 1,
              py: 0.5,
              mb: 2,
              fontWeight: 700,
              fontSize: '0.75rem',
              letterSpacing: '0.05em',
            }}
          />
          <Typography
            variant="h2"
            sx={{
              fontWeight: 800,
              mb: 2,
              color: '#0f172a',
              fontSize: { xs: '2rem', md: '2.75rem' },
            }}
          >
            Engineered for Modern UK Property Finance
          </Typography>
          <Typography
            variant="body1"
            sx={{
              maxWidth: 720,
              mx: 'auto',
              color: '#475569',
              fontSize: { xs: '1rem', md: '1.15rem' },
            }}
          >
            Replace fragmented Excel workbooks and slow legacy software with an institutional platform designed for UK private equity, developers, and lenders.
          </Typography>
        </Box>

        {/* Features Grid */}
        <Grid container spacing={3.5}>
          {ukFeatures.map((feat, index) => (
            <Grid item xs={12} md={6} lg={4} key={index}>
              <Card
                sx={{
                  height: '100%',
                  p: 3.5,
                  display: 'flex',
                  flexDirection: 'column',
                  justify: 'space-between',
                  background: '#ffffff',
                  border: '1px solid #e2e8f0',
                  borderRadius: 3.5,
                  boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.04)',
                  transition: 'all 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
                  position: 'relative',
                  overflow: 'hidden',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    borderColor: feat.highlightColor,
                    boxShadow: '0 10px 25px -5px rgba(15, 23, 42, 0.08)',
                  },
                }}
              >
                <Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                    <Box
                      sx={{
                        p: 1.5,
                        borderRadius: 2.5,
                        bgcolor: `${feat.highlightColor}10`,
                        color: feat.highlightColor,
                        display: 'inline-flex',
                        alignItems: 'center',
                        justify: 'center',
                      }}
                    >
                      {feat.icon}
                    </Box>
                    <Chip
                      label={feat.badge}
                      size="small"
                      sx={{
                        bgcolor: '#f8fafc',
                        color: '#64748b',
                        fontSize: '0.7rem',
                        fontWeight: 700,
                        border: '1px solid #e2e8f0',
                      }}
                    />
                  </Box>

                  <Typography variant="h5" sx={{ color: '#0f172a', fontWeight: 700, mb: 1.5 }}>
                    {feat.title}
                  </Typography>

                  <Typography variant="body2" sx={{ color: '#475569', lineHeight: 1.6 }}>
                    {feat.description}
                  </Typography>
                </Box>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Container>
    </Box>
  );
};

export default UkFeaturesGrid;
